import {
  LayoutGraph,
  HierarchicalLayout,
  HierarchicalLayoutConfig,
  LayoutNode,
  Size,
  LayoutGraphGrouping,
  HierarchicalLayoutRoutingStyle,
  HierarchicalLayoutEdgeDescriptor,
  TimeSpan,
} from '@ardoq/yfiles';
import { BlocksViewNode } from '../types';
import { isGroup } from '../viewModel$/util';
import { sortedIndexOf } from 'lodash';
import { isOdd } from '../misc/math';
import { logError } from '@ardoq/logging';
import { buildOrdinates } from './util';
import { maximumGraphLayoutDuration } from 'tabview/relationshipDiagrams/maximumGraphLayoutDuration';

const standardHierarchicLayoutOptions: HierarchicalLayoutConfig = {
  nodeLabelPlacement: 'consider',
  edgeLabelPlacement: 'integrated',
  defaultEdgeDescriptor: new HierarchicalLayoutEdgeDescriptor({
    backLoopRouting: false,
    routingStyleDescriptor: {
      defaultRoutingStyle: HierarchicalLayoutRoutingStyle.ORTHOGONAL,
    },
  }),
  minimumLayerDistance: 35,
  groupLayeringPolicy: 'recursive',
  fromScratchLayeringStrategy: 'hierarchical-optimal',

  /* my madey uppy stuff */
  // maximumDuration: 10000,
  // gridSpacing: 50,
  // componentLayoutEnabled: true,
  // componentArrangementPolicy: 'topmost',
};

function* leaves(node: BlocksViewNode): Iterable<BlocksViewNode> {
  if (isGroup(node)) {
    for (const child of node.children) {
      yield* leaves(child);
    }
  } else {
    yield node;
  }
}

function* groups(node: BlocksViewNode): Iterable<BlocksViewNode> {
  if (isGroup(node)) {
    yield node;

    for (const child of node.children) {
      yield* groups(child);
    }
  }
}

export const resetGroupSmart = (group: BlocksViewNode) => {
  const yNodes = new Map<BlocksViewNode, LayoutNode>();
  const nodes = new Map<LayoutNode, BlocksViewNode>();
  const yGraph = new LayoutGraph();

  function* ychildren(group: BlocksViewNode) {
    if (isGroup(group)) {
      for (const child of group.children) {
        yield yNodes.get(child)!;
      }
    }
  }

  const buildYNodes = (group: BlocksViewNode) => {
    for (const node of leaves(group)) {
      const yNode = yGraph.createNode();

      yNode.layout.size = new Size(100, 100);
      yNodes.set(node, yNode);
      nodes.set(yNode, node);
    }
    const grouping = LayoutGraphGrouping.replaceGrouping(yGraph);
    for (const node of groups(group)) {
      const cnt = node.descendantCount;
      const sz = Math.ceil(Math.sqrt(cnt));

      const yNode = yGraph.createNode();
      yNode.layout.size = new Size(100 * sz, 100 * sz);
      yNodes.set(node, yNode);
      nodes.set(yNode, node);
    }

    for (const ynode of yGraph.nodes) {
      const node = nodes.get(ynode);
      const yParent = node && node.parent ? yNodes.get(node.parent) : null;

      if (node && yParent) {
        grouping.setParent(ynode, yParent);
      }
    }

    for (const ynode of yGraph.nodes) {
      const node = nodes.get(ynode);

      if (node && node.children) {
        grouping.setIsGroupNode(ynode, true);
      }
    }
  };

  const buildYLinks = (node: BlocksViewNode) => {
    if (node.links) {
      for (const link of node.links) {
        const ySource = link.source === node ? yNodes.get(link.source) : null;
        const yTarget = ySource ? yNodes.get(link.target) : null;

        if (ySource && yTarget) {
          yGraph.createEdge(ySource, yTarget);
        }
      }
    }

    if (node.children) {
      for (const child of node.children) {
        buildYLinks(child);
      }
    }
  };

  const extractLayout = (group: BlocksViewNode) => {
    if (!group.children) {
      return;
    }

    for (const child of group.children) {
      extractLayout(child);
    }

    const epsilon = 0.1;
    const leftEdge = (ynode: LayoutNode) => ynode.layout.x;
    const topEdge = (ynode: LayoutNode) => ynode.layout.y;
    const rightEdge = (ynode: LayoutNode) =>
      ynode.layout.x + ynode.layout.width - epsilon;
    const bottomEdge = (ynode: LayoutNode) =>
      ynode.layout.y + ynode.layout.height - epsilon;

    const children = [...ychildren(group)];
    const cols = buildOrdinates(children, leftEdge, rightEdge);
    const rows = buildOrdinates(children, topEdge, bottomEdge);

    /* look up node layout in the ordinals */

    for (const child of group.children) {
      const ynode = yNodes.get(child)!;

      child.col = sortedIndexOf(cols, leftEdge(ynode));
      child.row = sortedIndexOf(rows, topEdge(ynode));
      child.colSpan = sortedIndexOf(cols, rightEdge(ynode)) - child.col;
      child.rowSpan = sortedIndexOf(rows, bottomEdge(ynode)) - child.row;

      if (!isOdd(child.col) || !isOdd(child.row)) {
        logError(Error('Unrecoverable parity error in resetGroupSmart.'));
      }

      if (!isOdd(child.colSpan) || !isOdd(child.rowSpan)) {
        logError(Error('Unrecoverable parity error in resetGroupSmart.'));
      }
    }
  };

  if (isGroup(group)) {
    buildYNodes(group);
    buildYLinks(group);

    // Set stopDuration based on the actual graph size
    const maxDuration = maximumGraphLayoutDuration(
      yGraph.nodes.size,
      yGraph.edges.size
    );
    const yLayout = new HierarchicalLayout({
      ...standardHierarchicLayoutOptions,
      stopDuration: TimeSpan.fromMilliseconds(maxDuration),
    });

    yLayout.applyLayout(yGraph);
    extractLayout(group);
  }
};
